import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPromptHistory } from '../../utils/promptHistory';
import {
  mergePromptHistory,
  filterLocalEntriesByScenario,
  filterPromptHistory,
  sortPromptHistory,
  type PromptHistoryPeriodFilter,
  type PromptHistorySortKey,
} from '../../utils/promptHistoryMerge';
import { formatDateTime } from '../../utils/formatters';
import { usePromptHistoryQuery } from '../../features/prompt-history/hooks';
import type { PromptHistoryScope } from '../../features/prompt-history/api';
import { SegmentedTabs } from './SegmentedTabs';
import { LoadMoreButton } from './LoadMoreButton';
import '../../styles/form-controls.css';
import styles from './PromptHistoryModal.module.css';

const PERIOD_OPTIONS: { value: PromptHistoryPeriodFilter; label: string }[] = [
  { value: '7d', label: '7日以内' },
  { value: '30d', label: '30日以内' },
  { value: 'all', label: '取得済み全件' },
];

const SORT_OPTIONS: { value: PromptHistorySortKey; label: string }[] = [
  { value: 'lastUsedAtDesc', label: '最終使用日時が新しい順' },
  { value: 'lastUsedAtAsc', label: '最終使用日時が古い順' },
  { value: 'useCountDesc', label: '使用回数が多い順' },
];

interface PromptHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** API scope (docs/features/prompt-history-db-plan.md の allowlist) */
  scope: PromptHistoryScope;
  /** localStorage キー (PROMPT_HISTORY_KEYS のいずれか) */
  storageKey: string;
  /** 履歴を選択したとき (呼び出し元で挿入してモーダルを閉じる) */
  onSelect: (prompt: string) => void;
  /** モーダルタイトル (既定「プロンプト履歴」) */
  title?: string;
  /**
   * シナリオ絞り込み (動画エディタ AI 編集の編集指示のみ)。指定すると「このシナリオ / すべてのシナリオ」の
   * 切り替えを表示し、既定で「このシナリオ」に絞る (DB はクエリ、ローカルは entry.scenarioId で絞る)。
   */
  scenarioFilter?: { scenarioId: string };
  /** 入れ子モーダル (modal-overlay--nested・z 1110) の上から開くとき true。既定の z (1010) だと呼び出し元の後ろに隠れる */
  aboveNested?: boolean;
}

type ScenarioFilterMode = 'scenario' | 'all';

const SCENARIO_OPTIONS: { value: ScenarioFilterMode; label: string }[] = [
  { value: 'scenario', label: 'このシナリオ' },
  { value: 'all', label: 'すべてのシナリオ' },
];

/**
 * プロンプト履歴の全画面モーダル (docs/features/prompt-history-db-plan.md
 * 「フロントエンド: 全画面モーダル + 二層データ」節)。
 *
 * localStorage (即時表示) + DB API (React Query) の二層データをマージして表示する。
 * スコープは呼び出し元に固定し、スコープ横断の一覧は作らない (誤挿入防止)。
 */
export function PromptHistoryModal({
  isOpen,
  onClose,
  scope,
  storageKey,
  onSelect,
  title = 'プロンプト履歴',
  scenarioFilter,
  aboveNested = false,
}: PromptHistoryModalProps) {
  const [localEntries, setLocalEntries] = useState(() => getPromptHistory(storageKey));
  const [keyword, setKeyword] = useState('');
  const [period, setPeriod] = useState<PromptHistoryPeriodFilter>('all');
  const [sortKey, setSortKey] = useState<PromptHistorySortKey>('lastUsedAtDesc');
  const [scenarioMode, setScenarioMode] = useState<ScenarioFilterMode>('scenario');

  // モーダルを開くたびに localStorage を読み直し、フィルタ条件をリセットする
  useEffect(() => {
    if (!isOpen) return;
    setLocalEntries(getPromptHistory(storageKey));
    setKeyword('');
    setPeriod('all');
    setSortKey('lastUsedAtDesc');
    setScenarioMode('scenario');
  }, [isOpen, storageKey]);

  // 絞り込み対象のシナリオ id (scenarioFilter 未指定・「すべてのシナリオ」のときは null)
  const activeScenarioId = scenarioFilter && scenarioMode === 'scenario' ? scenarioFilter.scenarioId : null;

  // Escape で閉じる + 背面スクロールロック
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  const query = usePromptHistoryQuery(scope, { enabled: isOpen, scenarioId: activeScenarioId });
  const dbItems = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);

  const scopedLocalEntries = useMemo(
    () => filterLocalEntriesByScenario(localEntries, activeScenarioId),
    [localEntries, activeScenarioId],
  );
  const merged = useMemo(() => mergePromptHistory(dbItems, scopedLocalEntries), [dbItems, scopedLocalEntries]);
  const filtered = useMemo(
    () => filterPromptHistory(merged, { keyword, period }),
    [merged, keyword, period],
  );
  const sorted = useMemo(() => sortPromptHistory(filtered, sortKey), [filtered, sortKey]);

  const handleSelect = useCallback((prompt: string) => {
    onSelect(prompt);
    onClose();
  }, [onSelect, onClose]);

  if (!isOpen) return null;

  // 初回取得中 (ローカルのみ表示中) かどうか。ページ追加取得中はここに含めない。
  const isInitialLoading = query.isLoading;
  const isError = query.isError && !query.isFetching;

  return createPortal(
    <div className={`${styles.backdrop} ${aboveNested ? styles.backdropAboveNested : ''}`} onMouseDown={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className={`${styles.modal} prompt-history-modal`} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <div className={styles.controls}>
          <label className={`form-label-common ${styles.searchBox}`}>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="キーワードで検索"
              aria-label="キーワードで検索"
            />
          </label>
          <div className={styles.filterGroup}>
            {scenarioFilter && (
              <SegmentedTabs
                options={SCENARIO_OPTIONS}
                value={scenarioMode}
                onChange={setScenarioMode}
                tone="neutral"
                ariaLabel="シナリオで絞り込む"
              />
            )}
            <SegmentedTabs
              options={PERIOD_OPTIONS}
              value={period}
              onChange={setPeriod}
              tone="neutral"
              ariaLabel="期間で絞り込む"
            />
            <select
              className={`form-select ${styles.sortSelect}`}
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as PromptHistorySortKey)}
              aria-label="並び替え"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {isInitialLoading && (
          <div className={`${styles.statusBanner} ${styles.statusBannerLoading}`}>
            <span>最新の履歴を取得中です (ローカルの履歴を表示しています)…</span>
          </div>
        )}
        {isError && (
          <div className={`${styles.statusBanner} ${styles.statusBannerError}`}>
            <span>履歴の取得に失敗しました。ローカルの履歴のみ表示しています。</span>
            <button type="button" className={styles.retryButton} onClick={() => query.refetch()}>
              再取得
            </button>
          </div>
        )}

        <div className={styles.body}>
          {sorted.length === 0 ? (
            <div className={styles.emptyState}>
              {merged.length === 0 ? '履歴はまだありません。' : '条件に一致する履歴がありません。'}
            </div>
          ) : (
            <ul className={styles.list}>
              {sorted.map((item) => (
                <li key={item.normalizedPrompt}>
                  <button
                    type="button"
                    className={styles.item}
                    onClick={() => handleSelect(item.prompt)}
                    title={item.prompt}
                  >
                    <span className={styles.itemText}>{item.prompt}</span>
                    <span className={styles.itemMeta}>
                      <span className={styles.itemMetaLeft}>
                        <span>{formatDateTime(item.lastUsedAt)}</span>
                        <span>使用回数: {item.useCountConfirmed ? item.useCount : '—'}</span>
                        {item.possiblyTruncated && <span className={styles.badge}>全文でない可能性</span>}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.footer}>
          <span className={styles.footerNote}>
            {isInitialLoading ? '取得中…' : `最新${dbItems.length}件を表示中`}
          </span>
          {query.hasNextPage && (
            <LoadMoreButton
              onClick={() => query.fetchNextPage()}
              isLoading={query.isFetchingNextPage}
              variant="secondary"
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default PromptHistoryModal;
